/**
 * Token Management Service for Rider Gateway (Mobile)
 * Mobile-optimized token storage with offline support and biometric hooks
 */

import { decodeJWT } from '../../utils/jwtUtils';
import { storageKeys, AuthError, mobileAuthSettings } from './authConfig';
import type { AuthTokens, JWTUser, AuthErrorInfo } from './authConfig';

export class TokenManager {
  private static instance: TokenManager;
  private refreshPromise: Promise<AuthTokens> | null = null;
  private offlineMode: boolean = false;

  private constructor() {
    this.initializeOfflineDetection();
  }

  public static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  /**
   * Initialize offline detection for mobile
   */
  private initializeOfflineDetection(): void {
    window.addEventListener('online', () => {
      this.offlineMode = false;
      console.log('Mobile app is back online');
    });

    window.addEventListener('offline', () => {
      this.offlineMode = true;
      console.log('Mobile app is offline');
    });

    this.offlineMode = !navigator.onLine;
  }

  /**
   * Store authentication tokens securely (mobile-optimized)
   */
  public storeTokens(tokens: AuthTokens): void {
    try {
      const expiresAt = Date.now() + (tokens.expiresIn * 1000);
      // const tokensWithExpiry = { ...tokens, expiresAt };

      // For mobile, use secure localStorage with encryption
      // In production, consider using secure storage libraries
      // const tokenData = {
      //   ...tokensWithExpiry,
      //   storedAt: Date.now(),
      // };

      // Store tokens (in production, encrypt these values)
      localStorage.setItem(storageKeys.accessToken, tokens.accessToken);
      localStorage.setItem(storageKeys.idToken, tokens.idToken);
      localStorage.setItem(storageKeys.refreshToken, tokens.refreshToken);

      // Store metadata
      localStorage.setItem('nebula_mobile_token_metadata', JSON.stringify({
        expiresAt,
        tokenType: tokens.tokenType,
        storedAt: Date.now(),
      }));

      console.log('Mobile tokens stored successfully');
    } catch (error) {
      console.error('Failed to store mobile tokens:', error);
      throw new Error('Failed to store authentication tokens');
    }
  }

  /**
   * Retrieve stored authentication tokens (mobile-optimized)
   */
  public getTokens(): AuthTokens | null {
    try {
      const accessToken = localStorage.getItem(storageKeys.accessToken);
      const idToken = localStorage.getItem(storageKeys.idToken);
      const refreshToken = localStorage.getItem(storageKeys.refreshToken);

      if (!accessToken || !idToken || !refreshToken) {
        return null;
      }

      const metadata = localStorage.getItem('nebula_mobile_token_metadata');
      if (!metadata) {
        return null;
      }

      const { expiresAt, tokenType, storedAt } = JSON.parse(metadata);

      // Check if tokens are too old for offline use
      if (this.offlineMode && mobileAuthSettings.enableOfflineMode) {
        const tokenAge = Date.now() - storedAt;
        if (tokenAge > mobileAuthSettings.maxOfflineCacheTime) {
          console.warn('Cached tokens too old for offline use');
          return null;
        }
      }

      return {
        accessToken,
        idToken,
        refreshToken,
        tokenType,
        expiresIn: Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)),
        expiresAt,
      };
    } catch (error) {
      console.error('Failed to retrieve mobile tokens:', error);
      return null;
    }
  }

  /**
   * Clear all stored tokens (mobile-optimized)
   */
  public clearTokens(): void {
    try {
      // Remove all token-related items
      Object.values(storageKeys).forEach(key => {
        localStorage.removeItem(key);
      });

      // Remove metadata
      localStorage.removeItem('nebula_mobile_token_metadata');

      // Clear biometric settings
      localStorage.removeItem('nebula_mobile_biometric_setup');

      console.log('Mobile tokens cleared successfully');
    } catch (error) {
      console.error('Failed to clear mobile tokens:', error);
    }
  }

  /**
   * Check if current tokens are valid (mobile-optimized)
   */
  public isTokenValid(): boolean {
    const tokens = this.getTokens();
    if (!tokens) {
      return false;
    }

    // In offline mode, use longer buffer time
    const bufferTime = this.offlineMode 
      ? mobileAuthSettings.tokenRefreshBuffer * 2 
      : mobileAuthSettings.tokenRefreshBuffer;
    
    return tokens.expiresAt > (Date.now() + bufferTime);
  }

  /**
   * Check if tokens need refresh (mobile-optimized)
   */
  public needsRefresh(): boolean {
    const tokens = this.getTokens();
    if (!tokens) {
      return false;
    }

    // Don't try to refresh in offline mode
    if (this.offlineMode) {
      return false;
    }

    // Refresh earlier on mobile due to network variability
    const refreshBuffer = mobileAuthSettings.tokenRefreshBuffer * 2; // 10 minutes
    return tokens.expiresAt <= (Date.now() + refreshBuffer);
  }

  /**
   * Check if app is in offline mode
   */
  public isOffline(): boolean {
    return this.offlineMode;
  }

  /**
   * Decode and validate ID token (mobile-optimized)
   */
  public decodeIdToken(idToken?: string): JWTUser | null {
    try {
      const token = idToken || this.getTokens()?.idToken;
      if (!token) {
        return null;
      }

      const decoded = decodeJWT(token);
      if (!decoded) {
        return null;
      }

      return {
        sub: decoded.sub || '',
        email: decoded.email || '',
        email_verified: decoded.email_verified === true,
        'custom:user_type': (decoded['custom:user_type'] as 'individual' | 'fleet' | 'staff' | 'admin') || 'individual',
        'cognito:groups': decoded['cognito:groups'] || [],
        given_name: decoded.given_name,
        family_name: decoded.family_name,
        phone_number: decoded.phone_number,
        'custom:full_name': decoded['custom:full_name'] || `${decoded.given_name || ''} ${decoded.family_name || ''}`.trim(),
      };
    } catch (error) {
      console.error('Failed to decode mobile ID token:', error);
      return null;
    }
  }

  /**
   * Store user information (mobile-optimized)
   */
  public storeUser(user: JWTUser): void {
    try {
      localStorage.setItem(storageKeys.user, JSON.stringify({
        ...user,
        lastUpdated: Date.now(),
      }));
    } catch (error) {
      console.error('Failed to store mobile user:', error);
    }
  }

  /**
   * Retrieve stored user information (mobile-optimized)
   */
  public getUser(): JWTUser | null {
    try {
      const userJson = localStorage.getItem(storageKeys.user);
      if (!userJson) return null;

      const userData = JSON.parse(userJson);
      
      // Remove metadata before returning
      const { lastUpdated, ...user } = userData;
      return user;
    } catch (error) {
      console.error('Failed to retrieve mobile user:', error);
      return null;
    }
  }

  /**
   * Generate code verifier (mobile-optimized)
   */
  public generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generate code challenge (mobile-optimized)
   */
  public async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generate state parameter (mobile-optimized)
   */
  public generateState(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Store PKCE parameters (mobile-optimized)
   */
  public storePKCEParams(codeVerifier: string, state: string): void {
    sessionStorage.setItem(storageKeys.codeVerifier, codeVerifier);
    sessionStorage.setItem(storageKeys.state, state);
  }

  /**
   * Retrieve and clear PKCE parameters (mobile-optimized)
   */
  public getPKCEParams(): { codeVerifier: string; state: string } | null {
    try {
      const codeVerifier = sessionStorage.getItem(storageKeys.codeVerifier);
      const state = sessionStorage.getItem(storageKeys.state);

      if (!codeVerifier || !state) {
        return null;
      }

      // Clear after retrieval
      sessionStorage.removeItem(storageKeys.codeVerifier);
      sessionStorage.removeItem(storageKeys.state);

      return { codeVerifier, state };
    } catch (error) {
      console.error('Failed to retrieve mobile PKCE params:', error);
      return null;
    }
  }

  /**
   * Validate state parameter (mobile-optimized)
   */
  public validateState(receivedState: string): boolean {
    const storedState = sessionStorage.getItem(storageKeys.state);
    return storedState === receivedState;
  }

  /**
   * Biometric authentication support (hooks for future implementation)
   */
  public async isBiometricEnabled(): Promise<boolean> {
    // Check if biometric authentication is available and enabled
    const enabled = localStorage.getItem(storageKeys.biometricEnabled);
    return enabled === 'true';
  }

  public async enableBiometric(): Promise<boolean> {
    // TODO: Implement biometric enrollment
    // This would integrate with WebAuthn or device biometric APIs
    try {
      // Placeholder for biometric setup
      localStorage.setItem(storageKeys.biometricEnabled, 'true');
      return true;
    } catch (error) {
      console.error('Failed to enable biometric auth:', error);
      return false;
    }
  }

  public async disableBiometric(): Promise<void> {
    localStorage.removeItem(storageKeys.biometricEnabled);
  }

  /**
   * Remember device functionality
   */
  public setRememberDevice(remember: boolean): void {
    localStorage.setItem(storageKeys.rememberDevice, remember.toString());
  }

  public shouldRememberDevice(): boolean {
    return localStorage.getItem(storageKeys.rememberDevice) === 'true';
  }

  /**
   * Refresh promise management (same as desktop but mobile-aware)
   */
  public setRefreshPromise(promise: Promise<AuthTokens>): void {
    this.refreshPromise = promise;
    promise.finally(() => {
      this.refreshPromise = null;
    });
  }

  public getRefreshPromise(): Promise<AuthTokens> | null {
    return this.refreshPromise;
  }

  /**
   * Create auth error (mobile-specific errors)
   */
  public createAuthError(type: AuthError, message: string, details?: any): AuthErrorInfo {
    return { type, message, details };
  }
}

// Create singleton instance
const tokenManager = TokenManager.getInstance();

// Export convenience functions
export const getAccessToken = (): string | null => {
  const tokens = tokenManager.getTokens();
  return tokens?.accessToken || null;
};

export const isTokenExpired = (): boolean => {
  return !tokenManager.isTokenValid();
};
